import { useReturnRegister } from "@/data/Content/RegisterReturn";
import { ContentContext } from "@/data/context/content";
import { useLocalization } from "@/data/Localization";
import { useForm } from "@/utils/useForm";
import { Button, Grid, Paper, Stack, TextField, Typography } from "@mui/material";
import { FC, useContext, useMemo } from "react";
import { ContentRecommendation } from "../../ContentRecommendation";
import { EMPTY_STRING } from "../../USPsDeliveryTerms";
import { ReturnOrderFormContainerSX, AddressCheckFormSX } from "../styles/returnOrder";
import { REG_EX } from "@/utils/address";
import { IfTrue } from "@/components/blocks/IfTrue";
import { usePathname } from "next/navigation";

export const ReturnAddressCheck: FC = () => {
    const returnOrderNLS = useLocalization('ReturnOrder');

    const {
        initReturnAddressValues,
        returnAddressValues:enteredValues,
        checkReturnOrderValid,
        buyerAdmin
    } = useContext(ContentContext) as ReturnType<typeof useReturnRegister>;

    const {
        handleInputChange,
        handleSubmit,
        submitting,
        values:returnAddressValues,
        error,
        formRef
    } = useForm(enteredValues.orderNumber?enteredValues:initReturnAddressValues);

    const helperText = useMemo(
        () => ({
            orderNumber: error.orderNumber ? returnOrderNLS.Errors.ERROR_RETURN_ORDER_NUMBER_EMPTY.t() : EMPTY_STRING,
            houseNumber: error.houseNumber ? returnOrderNLS.Errors.ERROR_RETURN_HOUSE_NUMBER_EMPTY.t() : EMPTY_STRING,
            postCode: error.postCode ? returnOrderNLS.Errors.ERROR_RETURN_ZIPCODE_EMPTY.t() : EMPTY_STRING,
        }),
        [returnOrderNLS, error]
    );

    const pathName = usePathname();
    const isGuestPage = !pathName?.includes("mijn-account");

    return (
        <Stack rowGap={2}>
            <Paper 
                sx={ReturnOrderFormContainerSX} 
                component="form" 
                onSubmit={handleSubmit(checkReturnOrderValid)}
                noValidate
                ref={formRef}
                elevation={3}
            >
                <Stack rowGap={2}>
                    <Stack rowGap={0.5}>
                        <Typography variant="h2" component="h2">{returnOrderNLS.AdressForm.OrderNumberText.t()}</Typography>
                        <Typography>*Verplicht veld</Typography>
                    </Stack>
                    <ContentRecommendation
                        id='IT-Returns-Header-eSpot'
                        properties={{
                            emsName:'IT-Returns-Header-eSpot'
                        }}
                    />
                </Stack>
                <Grid sx={AddressCheckFormSX}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={isGuestPage ? 6 : 9}>
                            <TextField
                                fullWidth
                                required
                                autoFocus
                                type="number"
                                name="orderNumber"
                                label={returnOrderNLS.Labels.orderNumber.t()}
                                value={returnAddressValues.orderNumber}
                                onChange={handleInputChange}
                                inputProps={{
                                    maxLength: 10
                                }}
                                error={error.orderNumber}
                                helperText={helperText.orderNumber}
                            />
                            <Typography variant="body1" m={0.5} mt={0}>
                                {returnOrderNLS.AdressForm.WhereToFindOrderNumber.t()}
                            </Typography>
                        </Grid>
                    </Grid>
                    <IfTrue condition={!buyerAdmin}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={isGuestPage ? 2 : 3}>
                                <TextField
                                    fullWidth
                                    required
                                    name="postCode"
                                    label={returnOrderNLS.Labels.postCode.t()}
                                    value={returnAddressValues.postCode}
                                    onChange={handleInputChange}
                                    inputProps={{
                                        maxLength: 7,
                                        pattern: REG_EX.ZIPCODE.source,
                                    }}
                                    error={error.postCode}
                                    helperText={helperText.postCode}
                                />
                            </Grid>
                            <Grid item xs={12} md={isGuestPage ? 4 : 6}>
                                <TextField
                                    fullWidth
                                    required
                                    name="houseNumber"
                                    label={
                                        <Typography component="span">
                                            <Typography component="span">{returnOrderNLS.Labels.houseNumber.t()}&nbsp;</Typography>
                                            <Typography component="span" sx={{fontSize:'16px !important'}}>{returnOrderNLS.Labels.houseNumberSubtext.t()}</Typography>
                                        </Typography>
                                    }
                                    value={returnAddressValues.houseNumber}
                                    onChange={handleInputChange}
                                    inputProps={{
                                        maxLength: 4,
                                    }}
                                    error={error.houseNumber}
                                    helperText={helperText.houseNumber}
                                    sx={{
                                        '& label': {
                                            whiteSpace:'unset',
                                            textOverflow:'unset'
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </IfTrue>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <Button
                                fullWidth
                                data-testid="register-address-check"
                                id="register-address-check"
                                type="submit"
                                disabled={submitting}
                                variant="clickThrough"
                                color="primary"
                            >
                                {returnOrderNLS.Labels.Submit.t()}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>
            <Paper elevation={3} sx={{p:3}}>
                <Grid container spacing={3}>
                    <Grid item>
                        <ContentRecommendation
                            id='IT-Returns-Register-Buttton-Text-espot'
                            properties={{
                                emsName:'IT-Returns-Register-Buttton-Text-espot'
                            }}
                        />
                    </Grid>
                    <Grid item>
                        <Button
                            variant="clickThrough"
                            href={returnOrderNLS.AdressForm.KlantenserviceLink.t()}
                            id="return-order-form-klantenservice-link"
                        >
                            {returnOrderNLS.AdressForm.Klantenservice.t()}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
        </Stack>
    );
};